import React, { useEffect, useState } from "react";

const UI = {
  spacing: { xs: 6, sm: 8, md: 12, lg: 16, xl: 24 },
  radius:  { sm: 6, md: 8, lg: 12 },
  colors: {
    primary:   "#0F172A",
    secondary: "#334155",
    muted:     "#64748B",
    subtle:    "#94A3B8",
    border:    "#E2E8F0",
    bg:        "#F8FAFC",
    white:     "#FFFFFF"
  }
};

const statusPills = {
  requested: { bg: "#EEF2FF", color: "#6366F1", text: "Requested" },
  assigned: { bg: "#E0F2FE", color: "#0369A1", text: "Assigned" },
  in_progress: { bg: "#FEF3C7", color: "#D97706", text: "In Progress" },
  completed: { bg: "#D1FAE5", color: "#059669", text: "Completed" },
  cancelled: { bg: "#F1F5F9", color: "#64748B", text: "Cancelled" },
  disputed: { bg: "#FEE2E2", color: "#DC2626", text: "Disputed" }
};

const paymentPills = {
  paid: { bg: "#D1FAE5", color: "#059669", text: "Paid" },
  pending: { bg: "#FEF3C7", color: "#D97706", text: "Pending" },
  refunded: { bg: "#F1F5F9", color: "#64748B", text: "Refunded" }
};

const avatarBg = (name) => {
  if (!name) return "#F3F4F6";
  const colors = ["#EEF2FF", "#E0F2FE", "#D1FAE5", "#FEF3C7", "#FEE2E2", "#F3E8FF", "#FFE4E6"];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const avatarColor = (name) => {
  if (!name) return "#9CA3AF";
  const colors = ["#4F46E5", "#0369A1", "#059669", "#D97706", "#DC2626", "#9333EA", "#E11D48"];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const initials = (name) => {
  if (!name) return "?";
  return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
};

const formatDateTime = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + " · " + 
         date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const DetailRow = ({ label, value }) => (
  <div style={{
    display: "flex",
    justifyContent: "space-between",
    padding: "9px 0",
    borderBottom: `1px solid ${UI.colors.bg}`
  }}>
    <span style={{
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontSize: 13,
      color: UI.colors.subtle
    }}>{label}</span>
    <span style={{
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontSize: 13,
      fontWeight: 600,
      color: UI.colors.secondary
    }}>{value || "—"}</span>
  </div>
);

const SectionLabel = ({ children }) => (
  <div style={{
    fontFamily: "'Playfair Display', serif",
    fontSize: 13,
    fontWeight: 700,
    color: UI.colors.muted,
    marginBottom: UI.spacing.md,
    textTransform: "uppercase",
    letterSpacing: "0.05em"
  }}>
    {children}
  </div>
);

const JobDrawer = ({ job, onClose, onUpdate, showToast, onCustomerClick, onProClick, onMarkComplete, onCancelJob, onRefundJob, onResolveDispute, onCloseCase }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [actionLoading, setActionLoading] = useState(false);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);
  
  const isAtRisk = job.status === "disputed" || (job.status === "completed" && job.paymentStatus === "pending") || (job.status === "cancelled" && job.timeline && job.timeline.length > 1);
  
  const handleAction = async (action, ...args) => {
    setActionLoading(true);
    await action(...args);
    setActionLoading(false);
  };
  
  if (!job) return null;
  
  return (
    <>
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1000
      }} onClick={onClose} />
      <div style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: isMobile ? "100vw" : 440,
        background: UI.colors.white,
        boxShadow: "-4px 0 16px rgba(0,0,0,0.1)",
        zIndex: 1001,
        display: "flex",
        flexDirection: "column",
        animation: "slideIn 0.2s ease"
      }}>
        <style>{`
          @keyframes slideIn {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>
        
        {/* Header */}
        <div style={{
          height: 60,
          background: UI.colors.bg,
          borderBottom: `1px solid ${UI.colors.border}`,
          padding: `0 ${UI.spacing.xl}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <div>
            <div style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 15,
              fontWeight: 700,
              color: UI.colors.primary
            }}>Job Details</div>
            <div style={{
              fontFamily: "monospace",
              fontSize: 12,
              color: UI.colors.muted
            }}>{job.jobId}</div>
          </div>
          <div style={{ display: "flex", gap: UI.spacing.sm, alignItems: "center" }}>
            <div style={{
              background: statusPills[job.status]?.bg || UI.colors.bg,
              color: statusPills[job.status]?.color || UI.colors.muted,
              padding: "3px 10px",
              borderRadius: 20,
              fontSize: 11,
              fontWeight: 700,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              {statusPills[job.status]?.text || job.status}
            </div>
            {isAtRisk && (
              <div style={{
                background: "#FEE2E2",
                color: "#DC2626",
                padding: "2px 8px",
                borderRadius: 20,
                fontSize: 10,
                fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                ⚠ At Risk
              </div>
            )}
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 16,
              fontWeight: 800,
              color: UI.colors.primary
            }}>KSh {job.price.toLocaleString()}</div>
            <button onClick={onClose} style={{
              width: 32,
              height: 32,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              background: UI.colors.white,
              cursor: "pointer",
              fontSize: 18
            }}>✕</button>
          </div>
        </div>
        
        {/* Scrollable Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: UI.spacing.xl }}>
          {/* Job Summary Card */}
          <div style={{
            background: UI.colors.bg,
            border: `1px solid ${UI.colors.border}`,
            borderRadius: UI.radius.lg,
            padding: UI.spacing.lg,
            marginBottom: UI.spacing.lg
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: UI.spacing.md
            }}>
              <span style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 15,
                fontWeight: 700,
                color: UI.colors.primary
              }}>{job.category}</span>
              <span style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 18,
                fontWeight: 800,
                color: UI.colors.primary
              }}>KSh {job.price.toLocaleString()}</span>
            </div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              color: UI.colors.muted,
              marginBottom: UI.spacing.sm
            }}>📍 {job.location}</div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              color: UI.colors.muted,
              marginBottom: UI.spacing.md
            }}>📅 {formatDate(job.date)} · {job.scheduledTime || "Time not set"}</div>
            <div>
              <span style={{
                background: paymentPills[job.paymentStatus]?.bg || UI.colors.bg,
                color: paymentPills[job.paymentStatus]?.color || UI.colors.muted,
                padding: "3px 10px",
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                display: "inline-block"
              }}>
                {paymentPills[job.paymentStatus]?.text || job.paymentStatus}
              </span>
            </div>
          </div>
          
          {/* Connected Entities */}
          <SectionLabel>CONNECTED ENTITIES</SectionLabel>
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: UI.spacing.md,
            marginBottom: UI.spacing.xl
          }}>
            {/* Customer Card */}
            <div onClick={() => { onCustomerClick(job); onClose(); }} style={{
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.lg,
              padding: UI.spacing.md,
              cursor: "pointer",
              transition: "border-color 0.15s ease"
            }} onMouseEnter={(e) => e.currentTarget.style.borderColor = "#6366F1"}
               onMouseLeave={(e) => e.currentTarget.style.borderColor = UI.colors.border}>
              <div style={{ display: "flex", alignItems: "center", gap: UI.spacing.md, marginBottom: UI.spacing.md }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: avatarBg(job.customer.name),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  fontSize: 14,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: avatarColor(job.customer.name)
                }}>
                  {initials(job.customer.name)}
                </div>
                <div>
                  <div style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 10,
                    fontWeight: 700,
                    color: UI.colors.subtle,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase"
                  }}>CUSTOMER</div>
                  <div style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    color: UI.colors.primary
                  }}>{job.customer.name}</div>
                  <div style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    color: UI.colors.muted
                  }}>{job.customer.phone}</div>
                </div>
              </div>
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 11,
                color: "#6366F1",
                textAlign: "right"
              }}>→ View Profile</div>
            </div>
            
            {/* Pro Card */}
            {job.pro ? (
              <div onClick={() => { onProClick(job); onClose(); }} style={{
                border: `1px solid ${UI.colors.border}`,
                borderRadius: UI.radius.lg,
                padding: UI.spacing.md,
                cursor: "pointer",
                transition: "border-color 0.15s ease"
              }} onMouseEnter={(e) => e.currentTarget.style.borderColor = "#6366F1"}
                 onMouseLeave={(e) => e.currentTarget.style.borderColor = UI.colors.border}>
                <div style={{ display: "flex", alignItems: "center", gap: UI.spacing.md, marginBottom: UI.spacing.md }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: avatarBg(job.pro.name),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                    fontSize: 14,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    color: avatarColor(job.pro.name)
                  }}>
                    {initials(job.pro.name)}
                  </div>
                  <div>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 10,
                      fontWeight: 700,
                      color: UI.colors.subtle,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase"
                    }}>PRO</div>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 13,
                      fontWeight: 600,
                      color: UI.colors.primary
                    }}>{job.pro.name}</div>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 12,
                      color: UI.colors.muted
                    }}>{job.pro.phone}</div>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 11,
                      color: "#F59E0B"
                    }}>★ {job.pro.rating}</div>
                  </div>
                </div>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 11,
                  color: "#6366F1",
                  textAlign: "right"
                }}>→ View Profile</div>
              </div>
            ) : (
              <div style={{
                border: `1px dashed ${UI.colors.border}`,
                borderRadius: UI.radius.lg,
                padding: UI.spacing.md,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 100
              }}>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  color: UI.colors.muted,
                  fontStyle: "italic"
                }}>Unassigned</div>
              </div>
            )}
          </div>
          
          {/* Dispute Section */}
          {job.dispute && (
            <div style={{ marginBottom: UI.spacing.xl }}>
              <SectionLabel>
                DISPUTE
                <span style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#DC2626",
                  marginLeft: UI.spacing.sm,
                  animation: "pulse 2s infinite"
                }} />
              </SectionLabel>
              <div style={{
                borderLeft: "4px solid #DC2626",
                background: "#FEF2F2",
                borderRadius: UI.radius.md,
                padding: UI.spacing.lg
              }}>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  color: UI.colors.secondary,
                  marginBottom: UI.spacing.sm
                }}>Raised by: {job.dispute.raisedBy}</div>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  color: UI.colors.secondary,
                  marginBottom: UI.spacing.sm
                }}>Reason: {job.dispute.reason}</div>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 12,
                  fontStyle: "italic",
                  color: UI.colors.muted,
                  marginBottom: UI.spacing.md
                }}>Notes: {job.dispute.notes}</div>
                <div style={{ display: "flex", gap: UI.spacing.sm }}>
                  <button onClick={() => handleAction(onResolveDispute, job.id)} disabled={actionLoading} style={{
                    padding: "6px 12px",
                    background: "#D1FAE5",
                    color: "#059669",
                    border: "none",
                    borderRadius: UI.radius.sm,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    cursor: actionLoading ? "not-allowed" : "pointer"
                  }}>✓ Resolve</button>
                  <button onClick={() => handleAction(onRefundJob, job.id)} disabled={actionLoading} style={{
                    padding: "6px 12px",
                    background: "#FEF3C7",
                    color: "#D97706",
                    border: "none",
                    borderRadius: UI.radius.sm,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    cursor: actionLoading ? "not-allowed" : "pointer"
                  }}>↩ Refund</button>
                  <button onClick={() => handleAction(onCloseCase, job.id)} disabled={actionLoading} style={{
                    padding: "6px 12px",
                    background: "#F1F5F9",
                    color: UI.colors.muted,
                    border: "none",
                    borderRadius: UI.radius.sm,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    cursor: actionLoading ? "not-allowed" : "pointer"
                  }}>✕ Close Case</button>
                </div>
              </div>
            </div>
          )}
          
          {/* Timeline */}
          <SectionLabel>JOB TIMELINE</SectionLabel>
          {job.timeline && job.timeline.length > 0 ? (
            <div style={{ marginBottom: UI.spacing.xl }}>
              {job.timeline.map((event, idx) => {
                let dotColor = "#6366F1";
                if (event.event === "Job Requested") dotColor = "#94A3B8";
                if (event.event === "Pro Assigned") dotColor = "#0369A1";
                if (event.event === "Job Started") dotColor = "#D97706";
                if (event.event === "Job Completed") dotColor = "#059669";
                if (event.event === "Cancelled") dotColor = "#64748B";
                
                return (
                  <div key={idx} style={{ display: "flex", gap: UI.spacing.md, position: "relative", paddingBottom: UI.spacing.md }}>
                    {idx < job.timeline.length - 1 && (
                      <div style={{
                        position: "absolute",
                        left: 5,
                        top: 14,
                        bottom: 0,
                        width: 1,
                        background: UI.colors.border
                      }} />
                    )}
                    <div style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: dotColor,
                      marginTop: 4,
                      flexShrink: 0,
                      zIndex: 1
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 13,
                        fontWeight: 600,
                        color: UI.colors.secondary,
                        marginBottom: 2
                      }}>{event.event}</div>
                      <div style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 11,
                        color: UI.colors.subtle
                      }}>{formatDateTime(event.time)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              color: UI.colors.subtle,
              textAlign: "center",
              padding: UI.spacing.lg,
              marginBottom: UI.spacing.xl
            }}>No timeline events recorded.</div>
          )}
          
          {/* Messages Preview */}
          <SectionLabel>MESSAGES</SectionLabel>
          {job.messages && job.messages.length > 0 ? (
            <div style={{ marginBottom: UI.spacing.xl }}>
              {job.messages.slice(-3).map((msg, idx) => (
                <div key={idx} style={{
                  padding: UI.spacing.md,
                  borderBottom: idx < 2 ? `1px solid ${UI.colors.border}` : "none"
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: UI.spacing.xs
                  }}>
                    <span style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      color: msg.sender === "customer" ? "#6366F1" : "#059669"
                    }}>{msg.sender === "customer" ? "CUSTOMER" : "PRO"}</span>
                    <span style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 11,
                      color: UI.colors.subtle
                    }}>{formatDateTime(msg.time)}</span>
                  </div>
                  <div style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 13,
                    color: UI.colors.secondary,
                    lineHeight: 1.5
                  }}>{msg.text}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              color: UI.colors.subtle,
              textAlign: "center",
              padding: UI.spacing.lg,
              marginBottom: UI.spacing.xl
            }}>No messages on this job.</div>
          )}
          
          {/* Payment Details */}
          <SectionLabel>PAYMENT</SectionLabel>
          <div style={{ marginBottom: UI.spacing.xl }}>
            <DetailRow label="Amount" value={`KSh ${job.price.toLocaleString()}`} />
            <DetailRow label="Payment Status" value={
              <span style={{
                background: paymentPills[job.paymentStatus]?.bg || UI.colors.bg,
                color: paymentPills[job.paymentStatus]?.color || UI.colors.muted,
                padding: "2px 8px",
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 700,
                display: "inline-block"
              }}>
                {paymentPills[job.paymentStatus]?.text || job.paymentStatus}
              </span>
            } />
            <DetailRow label="Payment Method" value={job.paymentMethod || "M-Pesa"} />
            <DetailRow label="Scheduled Date" value={formatDate(job.date)} />
          </div>
        </div>
        
        {/* Sticky Footer */}
        <div style={{
          borderTop: `1px solid ${UI.colors.border}`,
          padding: UI.spacing.lg,
          background: UI.colors.white
        }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: UI.spacing.md
          }}>
            <button
              onClick={() => handleAction(onCancelJob, job.id)}
              disabled={actionLoading || job.status === "completed" || job.status === "cancelled"}
              style={{
                padding: "10px 16px",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                borderRadius: UI.radius.md,
                border: "none",
                background: "#F1F5F9",
                color: UI.colors.muted,
                cursor: (actionLoading || job.status === "completed" || job.status === "cancelled") ? "not-allowed" : "pointer",
                opacity: (actionLoading || job.status === "completed" || job.status === "cancelled") ? 0.5 : 1
              }}
            >
              ⊘ Cancel Job
            </button>
            <button
              onClick={() => handleAction(onMarkComplete, job.id)}
              disabled={actionLoading || job.status === "completed"}
              style={{
                padding: "10px 16px",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                borderRadius: UI.radius.md,
                border: "none",
                background: "#D1FAE5",
                color: "#059669",
                cursor: (actionLoading || job.status === "completed") ? "not-allowed" : "pointer",
                opacity: (actionLoading || job.status === "completed") ? 0.5 : 1
              }}
            >
              ✓ Mark Complete
            </button>
            {job.paymentStatus === "paid" && (
              <button
                onClick={() => handleAction(onRefundJob, job.id)}
                disabled={actionLoading}
                style={{
                  padding: "10px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  borderRadius: UI.radius.md,
                  border: "none",
                  background: "#FEF3C7",
                  color: "#D97706",
                  cursor: actionLoading ? "not-allowed" : "pointer",
                  opacity: actionLoading ? 0.5 : 1
                }}
              >
                ↩ Refund
              </button>
            )}
            {job.status === "disputed" && (
              <button
                onClick={() => handleAction(onResolveDispute, job.id)}
                disabled={actionLoading}
                style={{
                  padding: "10px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  borderRadius: UI.radius.md,
                  border: "none",
                  background: "#FEE2E2",
                  color: "#DC2626",
                  cursor: actionLoading ? "not-allowed" : "pointer",
                  opacity: actionLoading ? 0.5 : 1
                }}
              >
                ⚑ Resolve Dispute
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default JobDrawer;